import type { JSX } from "react";
import { useEffect, useState } from "react";
import { Markdown } from "../components/chat/Markdown";
import { ArtifactDeck } from "./ArtifactDeck";
import {
  artifactApi,
  ArtifactError,
  SURFACE_LABEL,
  shareUrl,
  type ArtifactDetail,
} from "../lib/artifactApi";
import "../styles/artifacts.css";

interface ArtifactCanvasProps {
  /** The artifact to show; the canvas fetches its full content by id. */
  artifactId: string;
  onClose: () => void;
  /**
   * Whether promote-to-knowledge is available (7b). Grounding rides the
   * personal-KB retrieval, so the promote band shows only when personal
   * materials are enabled; false hides it (view/download stay).
   */
  promotable?: boolean;
  /** Wide reading mode: the canvas column takes more of the screen (the chat
   *  compresses further left). Drives the grid width on `.app` (App owns it). */
  expanded?: boolean;
  /** Toggle wide/narrow reading width. Absent → the expand control is hidden. */
  onToggleExpand?: () => void;
  /** 重新生成 (7b MVP): re-run the generation as a new VERSION of this artifact.
   *  App turns this into a chat turn carrying regenerateArtifactId. Absent → hidden. */
  onRegenerate?: (id: string, title: string) => void;
}

/**
 * Artifact side canvas (FEATURE_ARTIFACTS · task #7 · Phase 7a, design mockup ②).
 * A right-side panel (parallel to the citation RightRail) that renders a
 * generated artifact's markdown. Fetches the full content on open — the in-chat
 * card and Library rows carry only id/title. Read-only in 7a: view + download;
 * inline editing and promote-to-knowledge are later phases.
 */
export function ArtifactCanvas({
  artifactId,
  onClose,
  promotable = false,
  expanded = false,
  onToggleExpand,
  onRegenerate,
}: ArtifactCanvasProps): JSX.Element {
  const [artifact, setArtifact] = useState<ArtifactDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [promoteBusy, setPromoteBusy] = useState(false);
  const [promoteErr, setPromoteErr] = useState<string | null>(null);
  // Inline edit (P1): raw-markdown draft + manual 保存 (new version) / ✦AI润色.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saveBusy, setSaveBusy] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  // ✦AI润色 (P1): a server-side transform on the draft with a one-step undo. The
  // pre-polish draft is stashed so 撤销润色 restores it; it does NOT commit — 保存
  // is still the single commit path. Server route bypasses the chat 4000-char cap.
  const [polishBusy, setPolishBusy] = useState(false);
  const [polishPrev, setPolishPrev] = useState<string | null>(null);
  // 只读分享 (P2): outward-facing, so the panel gates on an explicit confirm. The
  // artifact's shareToken (from the owner GET) is the current state.
  const [shareOpen, setShareOpen] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [shareErr, setShareErr] = useState<string | null>(null);
  const [shareCopied, setShareCopied] = useState(false);
  // 文档 / 演示 (P3 Deck): the same markdown, rendered as slides in 演示 mode.
  const [deckMode, setDeckMode] = useState<"doc" | "present">("doc");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    setEditing(false);
    setSaveErr(null);
    artifactApi
      .get(artifactId)
      .then((a) => {
        if (!alive) return;
        if (a === null) setErr("这份文档不见了，可能已被删除。");
        setArtifact(a);
      })
      .catch((e: unknown) => {
        if (alive) setErr(e instanceof Error ? e.message : "打不开这份文档。");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [artifactId]);

  // Escape exits edit mode when editing (so a stray Escape doesn't discard a draft
  // by closing the whole canvas), otherwise closes the canvas.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== "Escape") return;
      if (editing) {
        setEditing(false);
        setPolishPrev(null);
      } else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, editing]);

  /** Enter edit mode with the current content as the draft. */
  function startEdit(): void {
    if (artifact === null) return;
    setDraft(artifact.content);
    setSaveErr(null);
    setPolishPrev(null);
    setEditing(true);
  }

  /** Leave edit mode, discarding the draft + any polish-undo state. */
  function cancelEdit(): void {
    setEditing(false);
    setPolishPrev(null);
    setSaveErr(null);
  }

  /** Save the draft as a new version (manual edit path — no model). */
  async function onSaveEdit(): Promise<void> {
    if (artifact === null) return;
    if (draft.trim().length === 0) {
      setSaveErr("内容不能为空。");
      return;
    }
    setSaveBusy(true);
    setSaveErr(null);
    try {
      const updated = await artifactApi.saveContent(artifact.id, draft);
      setArtifact(updated);
      setEditing(false);
      setPolishPrev(null);
    } catch (e) {
      setSaveErr(e instanceof ArtifactError ? e.message : "保存没成功，稍后再试。");
    }
    setSaveBusy(false);
  }

  /** ✦AI润色: server-side rewrite of the draft, applied in place with undo. */
  async function onPolishDraft(): Promise<void> {
    if (artifact === null || draft.trim().length === 0) return;
    setPolishBusy(true);
    setSaveErr(null);
    try {
      const polished = await artifactApi.polish(artifact.id, draft);
      setPolishPrev(draft); // stash for 撤销润色
      setDraft(polished);
    } catch (e) {
      setSaveErr(e instanceof ArtifactError ? e.message : "润色没成功，稍后再试。");
    }
    setPolishBusy(false);
  }

  /** 撤销润色: restore the pre-polish draft (one step). */
  function undoPolish(): void {
    if (polishPrev === null) return;
    setDraft(polishPrev);
    setPolishPrev(null);
  }

  const onDownload = (): void => {
    if (artifact === null) return;
    const blob = new Blob([artifact.content], { type: "text/markdown;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${artifact.title || "文档"}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const [copied, setCopied] = useState(false);
  const onCopy = async (): Promise<void> => {
    if (artifact === null) return;
    try {
      await navigator.clipboard.writeText(artifact.content);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard blocked (permissions / insecure context) — no-op, download stays.
    }
  };

  async function onPromote(): Promise<void> {
    if (artifact === null) return;
    setPromoteBusy(true);
    setPromoteErr(null);
    try {
      const updated = await artifactApi.promote(artifact.id);
      setArtifact(updated);
    } catch (e) {
      setPromoteErr(e instanceof ArtifactError ? e.message : "加入知识没成功，稍后再试。");
    }
    setPromoteBusy(false);
  }

  async function onUnpromote(): Promise<void> {
    if (artifact === null) return;
    setPromoteBusy(true);
    setPromoteErr(null);
    try {
      const updated = await artifactApi.unpromote(artifact.id);
      setArtifact(updated);
    } catch (e) {
      setPromoteErr(e instanceof ArtifactError ? e.message : "移出知识没成功，稍后再试。");
    }
    setPromoteBusy(false);
  }

  /** 生成只读链接 (explicit confirm before an outward-facing link is minted). */
  async function onShare(): Promise<void> {
    if (artifact === null) return;
    setShareBusy(true);
    setShareErr(null);
    try {
      setArtifact(await artifactApi.share(artifact.id));
    } catch (e) {
      setShareErr(e instanceof ArtifactError ? e.message : "生成分享链接没成功，稍后再试。");
    }
    setShareBusy(false);
  }

  /** 关闭分享: revoke the link immediately. */
  async function onUnshare(): Promise<void> {
    if (artifact === null) return;
    setShareBusy(true);
    setShareErr(null);
    try {
      setArtifact(await artifactApi.unshare(artifact.id));
    } catch (e) {
      setShareErr(e instanceof ArtifactError ? e.message : "关闭分享没成功，稍后再试。");
    }
    setShareBusy(false);
  }

  /** 重置链接: revoke the current token and mint a fresh one (old link dies). */
  async function onResetShare(): Promise<void> {
    if (artifact === null) return;
    setShareBusy(true);
    setShareErr(null);
    try {
      await artifactApi.unshare(artifact.id);
      setArtifact(await artifactApi.share(artifact.id));
    } catch (e) {
      setShareErr(e instanceof ArtifactError ? e.message : "重置链接没成功，稍后再试。");
    }
    setShareBusy(false);
  }

  /** Copy the current share link to the clipboard. */
  async function onCopyLink(): Promise<void> {
    if (artifact?.shareToken === undefined) return;
    try {
      await navigator.clipboard.writeText(shareUrl(artifact.shareToken));
      setShareCopied(true);
      window.setTimeout(() => setShareCopied(false), 1500);
    } catch {
      // Clipboard blocked — the link input stays selectable for manual copy.
    }
  }

  const versionLabel =
    artifact !== null && artifact.versions.length > 1 ? ` · v${artifact.versions.length}` : "";
  const surfaceLabel = artifact !== null ? SURFACE_LABEL[artifact.surface] : "文档";

  return (
    <aside className="rail artifact-canvas" data-testid="artifact-canvas">
      <div className="rail-head">
        <div className="rail-label">
          {artifact?.title ?? (loading ? "打开中…" : "文档")}
          <span className="artifact-canvas-sub">
            {surfaceLabel}
            {versionLabel}
            {editing ? " · 编辑中" : ""}
          </span>
        </div>
        <div className="artifact-canvas-head-acts">
          {/* In edit mode the doc actions give way to the edit toolbar (in body);
              only 关闭 stays in the head. */}
          {!editing && (
            <>
              {onToggleExpand !== undefined && (
                <button
                  type="button"
                  className="artifact-btn"
                  data-testid="artifact-canvas-expand"
                  aria-pressed={expanded}
                  onClick={onToggleExpand}
                >
                  {expanded ? "收起" : "展开"}
                </button>
              )}
              {artifact !== null && (
                <span className="artifact-seg" role="tablist" aria-label="视图">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={deckMode === "doc"}
                    className={deckMode === "doc" ? "on" : ""}
                    data-testid="artifact-view-doc"
                    onClick={() => setDeckMode("doc")}
                  >
                    文档
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={deckMode === "present"}
                    className={deckMode === "present" ? "on" : ""}
                    data-testid="artifact-view-present"
                    onClick={() => setDeckMode("present")}
                  >
                    演示
                  </button>
                </span>
              )}
              {/* Doc-mode actions (edit/share/copy/download) give way to the deck's
                  own export bar in 演示 mode. */}
              {artifact !== null && deckMode === "doc" && (
                <button
                  type="button"
                  className="artifact-btn"
                  data-testid="artifact-canvas-edit"
                  onClick={startEdit}
                >
                  编辑
                </button>
              )}
              {artifact !== null && deckMode === "doc" && onRegenerate !== undefined && (
                <button
                  type="button"
                  className="artifact-btn"
                  data-testid="artifact-canvas-regenerate"
                  onClick={() => onRegenerate(artifact.id, artifact.title)}
                >
                  重新生成
                </button>
              )}
              {artifact !== null && deckMode === "doc" && (
                <button
                  type="button"
                  className="artifact-btn"
                  data-testid="artifact-canvas-share"
                  aria-pressed={shareOpen}
                  onClick={() => setShareOpen((v) => !v)}
                >
                  {artifact.shareToken !== undefined ? "分享中" : "分享"}
                </button>
              )}
              {artifact !== null && deckMode === "doc" && (
                <button
                  type="button"
                  className="artifact-btn"
                  data-testid="artifact-canvas-copy"
                  onClick={() => void onCopy()}
                >
                  {copied ? "已复制" : "复制"}
                </button>
              )}
              {artifact !== null && deckMode === "doc" && (
                <button
                  type="button"
                  className="artifact-btn"
                  data-testid="artifact-canvas-download"
                  onClick={onDownload}
                >
                  下载
                </button>
              )}
            </>
          )}
          <button className="rail-close" type="button" aria-label="关闭" onClick={onClose}>
            ×
          </button>
        </div>
      </div>
      <div className="artifact-canvas-body">
        {loading ? (
          <div className="artifact-canvas-note">加载中…</div>
        ) : err !== null ? (
          <div className="artifact-canvas-note">{err}</div>
        ) : editing && artifact !== null ? (
          <div className="artifact-edit" data-testid="artifact-canvas-edit-mode">
            <div className="artifact-edit-bar">
              <span className="artifact-edit-hint">直接改，或让小环润色一版</span>
              <div className="artifact-edit-acts">
                <button
                  type="button"
                  className="artifact-btn"
                  disabled={saveBusy || polishBusy}
                  data-testid="artifact-edit-cancel"
                  onClick={cancelEdit}
                >
                  取消
                </button>
                {polishPrev !== null ? (
                  <button
                    type="button"
                    className="artifact-btn"
                    disabled={saveBusy || polishBusy}
                    data-testid="artifact-edit-undo-polish"
                    onClick={undoPolish}
                  >
                    撤销润色
                  </button>
                ) : (
                  <button
                    type="button"
                    className="artifact-btn"
                    disabled={saveBusy || polishBusy || draft.trim().length === 0}
                    data-testid="artifact-edit-polish"
                    onClick={() => void onPolishDraft()}
                  >
                    {polishBusy ? "润色中…" : "✦ AI 润色"}
                  </button>
                )}
                <button
                  type="button"
                  className="artifact-btn artifact-btn-pri"
                  disabled={saveBusy || polishBusy}
                  data-testid="artifact-edit-save"
                  onClick={() => void onSaveEdit()}
                >
                  {saveBusy ? "保存中…" : "保存"}
                </button>
              </div>
            </div>
            {saveErr !== null && (
              <div className="artifact-canvas-note" role="alert">
                {saveErr}
              </div>
            )}
            <textarea
              className="artifact-edit-area"
              value={draft}
              autoFocus
              spellCheck={false}
              disabled={saveBusy || polishBusy}
              data-testid="artifact-edit-area"
              onChange={(e) => setDraft(e.target.value)}
            />
          </div>
        ) : artifact !== null && deckMode === "present" ? (
          <ArtifactDeck markdown={artifact.content} title={artifact.title} />
        ) : artifact !== null ? (
          <>
            {shareOpen && (
              <div className="artifact-share" data-testid="artifact-share-panel">
                <div className="artifact-share-h">分享「{artifact.title}」</div>
                {artifact.shareToken === undefined ? (
                  <>
                    <div className="artifact-share-d">
                      生成一个只读链接，任何拿到链接的人都能查看（不能编辑）。
                    </div>
                    <button
                      type="button"
                      className="artifact-btn artifact-btn-pri"
                      disabled={shareBusy}
                      data-testid="artifact-share-create"
                      onClick={() => void onShare()}
                    >
                      {shareBusy ? "生成中…" : "生成只读链接"}
                    </button>
                  </>
                ) : (
                  <>
                    <input
                      className="artifact-share-link"
                      readOnly
                      value={shareUrl(artifact.shareToken)}
                      data-testid="artifact-share-link"
                      onFocus={(e) => e.currentTarget.select()}
                    />
                    <div className="artifact-share-acts">
                      <button
                        type="button"
                        className="artifact-btn artifact-btn-pri"
                        data-testid="artifact-share-copy"
                        onClick={() => void onCopyLink()}
                      >
                        {shareCopied ? "已复制" : "复制链接"}
                      </button>
                      <button
                        type="button"
                        className="artifact-btn"
                        disabled={shareBusy}
                        data-testid="artifact-share-reset"
                        onClick={() => void onResetShare()}
                      >
                        重置链接
                      </button>
                      <button
                        type="button"
                        className="artifact-btn"
                        disabled={shareBusy}
                        data-testid="artifact-share-close"
                        onClick={() => void onUnshare()}
                      >
                        关闭分享
                      </button>
                    </div>
                    <div className="artifact-share-meta">
                      <span className="artifact-share-ro">● 只读</span>
                      <span>· 可随时「关闭分享」使链接失效</span>
                    </div>
                  </>
                )}
                {shareErr !== null && (
                  <div className="artifact-canvas-note" role="alert">
                    {shareErr}
                  </div>
                )}
              </div>
            )}
            {promotable &&
              (artifact.promoted ? (
                <div className="artifact-promote artifact-promote-done" data-testid="artifact-promoted">
                  <span className="artifact-promote-txt">已入知识 · 小环答题时会自动参考它</span>
                  <button
                    type="button"
                    className="artifact-promote-undo"
                    disabled={promoteBusy}
                    onClick={() => void onUnpromote()}
                    data-testid="artifact-unpromote"
                  >
                    移出知识
                  </button>
                </div>
              ) : (
                <div className="artifact-promote">
                  <span className="artifact-promote-txt">存好了。想让小环以后答题自动参考它？</span>
                  <button
                    type="button"
                    className="artifact-promote-btn"
                    disabled={promoteBusy}
                    onClick={() => void onPromote()}
                    data-testid="artifact-promote"
                  >
                    {promoteBusy ? "处理中…" : "加入知识"}
                  </button>
                </div>
              ))}
            {promoteErr !== null && (
              <div className="artifact-canvas-note" role="alert">
                {promoteErr}
              </div>
            )}
            <div className="md artifact-canvas-doc">
              <Markdown>{artifact.content}</Markdown>
            </div>
          </>
        ) : null}
      </div>
    </aside>
  );
}
